import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { format, differenceInDays } from 'date-fns';
import { de } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { 
  MapPin, 
  Calendar, 
  CreditCard,
  Upload,
  AlertCircle,
  Loader2,
  CheckCircle,
  Package,
  Tag,
  Printer,
  Ruler
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CheckoutPage = () => {
  const { t } = useTranslation();
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  
  const bookingData = location.state;
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [booking, setBooking] = useState(null);
  const [createdBookingId, setCreatedBookingId] = useState(bookingId !== 'new' ? bookingId : null);
  const [services, setServices] = useState([]);
  const [printService, setPrintService] = useState(null);
  const [printOption, setPrintOption] = useState('none');
  const [trailerDimensions, setTrailerDimensions] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    notes: '',
    discount_code: ''
  });
  const [discountInfo, setDiscountInfo] = useState(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountError, setDiscountError] = useState('');

  useEffect(() => {
    fetchServices();
    if (bookingId && bookingId !== 'new') {
      fetchBooking();
    }
    // Fetch trailer dimensions for self-print option
    if (bookingData?.locationId) {
      fetchTrailerDimensions(bookingData.locationId);
      // Track quote lead for abandoned quote emails
      if (token) {
        fetch(`${API_URL}/api/quote-lead`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ location_id: bookingData.locationId })
        }).catch(() => {});
      }
    }
  }, [bookingId, bookingData?.locationId]);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: prev.name || user.name || '',
        email: prev.email || user.email || '',
        phone: prev.phone || user.phone || ''
      }));
    }
  }, [user]);

  const fetchServices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/services`);
      if (response.ok) {
        const data = await response.json();
        setServices(data.services || []);
        setPrintService(data.print_service || null);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchTrailerDimensions = async (locationId) => {
    try {
      const response = await fetch(`${API_URL}/api/locations/${locationId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.trailer) {
          setTrailerDimensions(data.trailer);
        }
      }
    } catch (error) {
      console.error('Error fetching trailer dimensions:', error);
    }
  };

  const fetchBooking = async () => {
    try {
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}`);
      if (!response.ok) throw new Error('Booking not found');
      const data = await response.json();
      setBooking(data);
    } catch (error) {
      console.error('Error fetching booking:', error);
      setError('Buchung nicht gefunden');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleService = (serviceId) => {
    setSelectedServices(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const calculateServicesPrice = () => {
    return services
      .filter(s => selectedServices.includes(s.id))
      .reduce((sum, s) => sum + s.price, 0);
  };

  const validateDiscount = async () => {
    if (!formData.discount_code.trim()) return;
    setDiscountLoading(true);
    setDiscountError('');
    setDiscountInfo(null);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(`${API_URL}/api/discount/validate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          code: formData.discount_code.trim(),
          booking_total: basePrice + calculateServicesPrice()
        })
      });
      if (response.ok) {
        const data = await response.json();
        setDiscountInfo(data);
      } else {
        try {
          const err = await response.json();
          setDiscountError(err.detail || 'Ungültiger Rabattcode');
        } catch {
          setDiscountError('Ungültiger Rabattcode');
        }
      }
    } catch (e) {
      setDiscountError('Fehler beim Prüfen des Rabattcodes');
    } finally {
      setDiscountLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!startDate || !endDate || endDate <= startDate) {
        throw new Error('Bitte wählen Sie einen gültigen Zeitraum.');
      }

      let currentBookingId = bookingId !== 'new' ? bookingId : createdBookingId;

      if (bookingId === 'new' && bookingData && !currentBookingId) {
        const headers = { 'Content-Type': 'application/json' };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const createResponse = await fetch(`${API_URL}/api/bookings`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            location_id: bookingData.locationId,
            start_date: bookingData.startDate,
            end_date: bookingData.endDate,
            customer_email: formData.email,
            customer_name: formData.name,
            customer_phone: formData.phone,
            notes: formData.notes,
            services: selectedServices,
            discount_code: discountInfo ? discountInfo.code : (formData.discount_code.trim() || null),
            print_option: printOption
          })
        });

        if (!createResponse.ok) {
          const errorData = await createResponse.json();
          throw new Error(errorData.detail || 'Fehler beim Erstellen der Buchung');
        }

        const newBooking = await createResponse.json();
        currentBookingId = newBooking.id;
        setCreatedBookingId(newBooking.id);
        setBooking(newBooking);
        navigate(`/checkout/${newBooking.id}`, { replace: true });
      }

      if (!currentBookingId) {
        throw new Error('Die Reservierung konnte nicht vorbereitet werden.');
      }

      const checkoutResponse = await fetch(`${API_URL}/api/checkout/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_id: currentBookingId,
          origin_url: window.location.origin
        })
      });

      if (!checkoutResponse.ok) {
        const errorData = await checkoutResponse.json();
        throw new Error(errorData.detail || 'Fehler beim Erstellen der Checkout-Session');
      }

      const { checkout_url } = await checkoutResponse.json();
      window.location.href = checkout_url;

    } catch (error) {
      console.error('Checkout error:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  };

  const displayData = booking || bookingData;
  const startDate = displayData?.startDate ? new Date(displayData.startDate) : (displayData?.start_date ? new Date(displayData.start_date) : null);
  const endDate = displayData?.endDate ? new Date(displayData.endDate) : (displayData?.end_date ? new Date(displayData.end_date) : null);
  const days = startDate && endDate ? differenceInDays(endDate, startDate) : 0;
  const basePrice = displayData?.price || displayData?.total_price || 0;
  const servicesPrice = calculateServicesPrice();
  const printPrice = printOption === 'service' && printService ? printService.price : 0;
  const discountAmount = discountInfo ? discountInfo.discount_amount : 0;
  const totalPrice = Math.max(0, basePrice + servicesPrice + printPrice - discountAmount);
  const locationName = displayData?.locationName || displayData?.location?.name || '';

  if (!displayData) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-[hsl(var(--destructive))]" />
          <h2 className="text-xl font-semibold mb-2">Keine Buchungsdaten</h2>
          <p className="text-[hsl(var(--muted-foreground))] mb-4">Bitte wählen Sie zuerst einen Standort und Zeitraum aus.</p>
          <Button onClick={() => navigate('/locations')}>Zu den Standorten</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8 lg:py-12 bg-[hsl(var(--surface-2))]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="text-3xl font-semibold mb-8">{t('checkout.title')}</h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Customer Details */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('checkout.yourDetails')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6" id="checkout-form">
                    {error && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name">{t('checkout.name')} *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          placeholder="Max Muster"
                          data-testid="checkout-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">{t('checkout.email')} *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          placeholder="max@beispiel.ch"
                          data-testid="checkout-email-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">{t('checkout.phone')}</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+41 79 123 45 67"
                        data-testid="checkout-phone-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="notes">{t('checkout.notes')}</Label>
                      <Textarea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        placeholder={t('checkout.notesPlaceholder')}
                        rows={3}
                        data-testid="checkout-notes-input"
                      />
                    </div>

                    {/* Discount Code */}
                    <div className="space-y-2">
                      <Label htmlFor="discount_code" className="flex items-center gap-1">
                        <Tag className="w-4 h-4" />
                        Rabattcode
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          id="discount_code"
                          name="discount_code"
                          value={formData.discount_code}
                          onChange={handleInputChange}
                          placeholder="z.B. DANKE-ABC123"
                          className="font-mono uppercase"
                          data-testid="checkout-discount-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={validateDiscount}
                          disabled={!formData.discount_code.trim() || discountLoading}
                          data-testid="checkout-discount-apply"
                        >
                          {discountLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Einlösen'}
                        </Button>
                      </div>
                      {discountInfo && (
                        <div className="flex items-center gap-2 text-sm text-emerald-600" data-testid="discount-applied">
                          <CheckCircle className="w-4 h-4" />
                          <span>Rabatt: {discountInfo.label} (-CHF {formatPrice(discountInfo.discount_amount)})</span>
                        </div>
                      )}
                      {discountError && (
                        <p className="text-sm text-red-600">{discountError}</p>
                      )}
                    </div>
                  </form>
                </CardContent>
              </Card>

              {/* Additional Services */}
              {services.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Package className="w-5 h-5 mr-2" />
                      Zusatzservices (optional)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {services.map((service) => (
                        <div 
                          key={service.id} 
                          className={`flex items-start gap-4 p-4 rounded-lg border transition-colors cursor-pointer ${
                            selectedServices.includes(service.id) 
                              ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.05)]' 
                              : 'border-[hsl(var(--border))] hover:border-[hsl(var(--primary)/0.5)]'
                          }`}
                          onClick={() => toggleService(service.id)}
                          data-testid={`service-${service.id}`}
                        >
                          <Checkbox 
                            checked={selectedServices.includes(service.id)}
                            onCheckedChange={() => toggleService(service.id)}
                          />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h4 className="font-medium">{service.name}</h4>
                              <span className="font-semibold">CHF {formatPrice(service.price)}</span>
                            </div>
                            <p className="text-sm text-[hsl(var(--muted-foreground))]">{service.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Print Option */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Printer className="w-5 h-5 mr-2" />
                    Druckoption
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Option: We print */}
                    {printService?.enabled && (
                      <div
                        className={`flex items-start gap-4 p-4 rounded-lg border transition-colors cursor-pointer ${
                          printOption === 'service'
                            ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.05)]'
                            : 'border-[hsl(var(--border))] hover:border-[hsl(var(--primary)/0.5)]'
                        }`}
                        onClick={() => setPrintOption('service')}
                        data-testid="print-option-service"
                      >
                        <Checkbox
                          checked={printOption === 'service'}
                          onCheckedChange={() => setPrintOption(printOption === 'service' ? 'none' : 'service')}
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium">Wir drucken für Sie</h4>
                            <span className="font-semibold">CHF {formatPrice(printService.price)}</span>
                          </div>
                          <p className="text-sm text-[hsl(var(--muted-foreground))]">
                            {printService.description}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Option: Self print */}
                    <div
                      className={`flex items-start gap-4 p-4 rounded-lg border transition-colors cursor-pointer ${
                        printOption === 'self'
                          ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.05)]'
                          : 'border-[hsl(var(--border))] hover:border-[hsl(var(--primary)/0.5)]'
                      }`}
                      onClick={() => setPrintOption('self')}
                      data-testid="print-option-self"
                    >
                      <Checkbox
                        checked={printOption === 'self'}
                        onCheckedChange={() => setPrintOption(printOption === 'self' ? 'none' : 'self')}
                      />
                      <div className="flex-1">
                        <h4 className="font-medium">Ich drucke selbst</h4>
                        <p className="text-sm text-[hsl(var(--muted-foreground))]">
                          Drucken Sie Ihr Plakat selbst — die Masse und Ösen-Infos finden Sie unten.
                        </p>
                      </div>
                    </div>

                    {/* Trailer Dimensions (shown when self-print is selected) */}
                    {printOption === 'self' && trailerDimensions && (
                      <div className="mt-3 p-4 rounded-lg bg-[hsl(var(--accent))] border border-[hsl(var(--border))]" data-testid="trailer-dimensions-info">
                        <div className="flex items-center gap-2 mb-3">
                          <Ruler className="w-4 h-4 text-[hsl(var(--primary))]" />
                          <h4 className="font-semibold text-sm">Druckmasse für: {trailerDimensions.name}</h4>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          {trailerDimensions.print_width_mm > 0 && (
                            <div>
                              <span className="text-[hsl(var(--muted-foreground))]">Breite:</span>
                              <span className="ml-1 font-semibold">{trailerDimensions.print_width_mm} mm</span>
                            </div>
                          )}
                          {trailerDimensions.print_height_mm > 0 && (
                            <div>
                              <span className="text-[hsl(var(--muted-foreground))]">Höhe:</span>
                              <span className="ml-1 font-semibold">{trailerDimensions.print_height_mm} mm</span>
                            </div>
                          )}
                          {trailerDimensions.eyelet_count > 0 && (
                            <div>
                              <span className="text-[hsl(var(--muted-foreground))]">Ösen:</span>
                              <span className="ml-1 font-semibold">{trailerDimensions.eyelet_count} Stück</span>
                            </div>
                          )}
                          {trailerDimensions.eyelet_spacing_mm > 0 && (
                            <div>
                              <span className="text-[hsl(var(--muted-foreground))]">Ösenabstand:</span>
                              <span className="ml-1 font-semibold">{trailerDimensions.eyelet_spacing_mm} mm</span>
                            </div>
                          )}
                        </div>
                        {trailerDimensions.dimension_notes && (
                          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2 italic">
                            {trailerDimensions.dimension_notes}
                          </p>
                        )}
                        {(!trailerDimensions.print_width_mm && !trailerDimensions.print_height_mm) && (
                          <p className="text-sm text-amber-600">
                            Dimensionen noch nicht hinterlegt. Bitte kontaktieren Sie uns für die genauen Masse.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Design Upload */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('checkout.uploadDesign')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="border-2 border-dashed border-[hsl(var(--border))] rounded-lg p-8 text-center">
                    <Upload className="w-8 h-8 mx-auto mb-2 text-[hsl(var(--muted-foreground))]" />
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">
                      {t('checkout.uploadHint')}
                    </p>
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" id="design-upload" />
                    <Button type="button" variant="outline" className="mt-4" onClick={() => document.getElementById('design-upload').click()}>
                      Datei auswählen
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Terms & Submit */}
              <div className="space-y-4">
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  {t('checkout.termsAgree')}{' '}
                  <a href="/terms" className="text-[hsl(var(--primary))] hover:underline">{t('checkout.terms')}</a>
                  {' '}{t('checkout.and')}{' '}
                  <a href="/privacy" className="text-[hsl(var(--primary))] hover:underline">{t('checkout.privacy')}</a>.
                </p>

                <Button
                  type="submit"
                  form="checkout-form"
                  size="lg"
                  className="w-full"
                  disabled={loading || !formData.name || !formData.email}
                  data-testid="checkout-submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('checkout.processing')}
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      {t('checkout.payNow')} - CHF {formatPrice(totalPrice)}
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Summary */}
            <div>
              <Card className="sticky top-24" data-testid="checkout-price-breakdown">
                <CardHeader>
                  <CardTitle>{t('checkout.yourBooking')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Location */}
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-[hsl(var(--primary))] mt-0.5" />
                    <div>
                      <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('checkout.location')}</p>
                      <p className="font-medium">{locationName}</p>
                    </div>
                  </div>

                  {/* Period */}
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-[hsl(var(--primary))] mt-0.5" />
                    <div>
                      <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('checkout.period')}</p>
                      <p className="font-medium">
                        {startDate && format(startDate, 'dd. MMM yyyy', { locale: de })}
                        {' - '}
                        {endDate && format(endDate, 'dd. MMM yyyy', { locale: de })}
                      </p>
                    </div>
                  </div>

                  {/* Duration */}
                  <div className="pt-4 border-t border-[hsl(var(--border))]">
                    <div className="flex justify-between text-sm">
                      <span className="text-[hsl(var(--muted-foreground))]">{t('checkout.duration')}</span>
                      <span>{days} {t('checkout.days')} ({Math.round(days/7 * 10) / 10} {t('checkout.weeks')})</span>
                    </div>
                  </div>

                  {/* Price Breakdown */}
                  <div className="pt-4 border-t border-[hsl(var(--border))] space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-[hsl(var(--muted-foreground))]">Werbefläche</span>
                      <span>CHF {formatPrice(basePrice)}</span>
                    </div>
                    {selectedServices.length > 0 && (
                      <>
                        {services.filter(s => selectedServices.includes(s.id)).map(service => (
                          <div key={service.id} className="flex justify-between text-sm">
                            <span className="text-[hsl(var(--muted-foreground))]">{service.name}</span>
                            <span>CHF {formatPrice(service.price)}</span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>

                  {/* Print Service */}
                  {printOption === 'service' && printService && (
                    <div className="flex justify-between text-sm">
                      <span className="text-[hsl(var(--muted-foreground))]">Druck & Montage</span>
                      <span>CHF {formatPrice(printService.price)}</span>
                    </div>
                  )}
                  {printOption === 'self' && (
                    <div className="flex justify-between text-sm text-[hsl(var(--muted-foreground))]">
                      <span>Selbstdruck</span>
                      <span>CHF 0.00</span>
                    </div>
                  )}

                  {/* Discount */}
                  {discountInfo && (
                    <div className="flex justify-between text-sm text-emerald-600">
                      <span>Rabatt ({discountInfo.label})</span>
                      <span>- CHF {formatPrice(discountInfo.discount_amount)}</span>
                    </div>
                  )}

                  {/* Total */}
                  <div className="pt-4 border-t border-[hsl(var(--border))]">
                    <div className="flex justify-between text-lg font-semibold">
                      <span>{t('checkout.total')}</span>
                      <span>CHF {formatPrice(totalPrice)}</span>
                    </div>
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">inkl. MwSt.</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CheckoutPage;
